import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthState } from 'state/useAuthState';
import { API } from 'api';
import { registerDeviceWithWatchDuty } from 'shared/device';
import { enqueueSnackbar } from 'notistack';
import {
  logUnexpectedErrorAndNotifyUser,
  logUnexpectedErrorAndNotifyUserWithTryAgain,
} from 'shared/handleUnexpectedError';
import { useSSORequired } from 'features/sso/hooks/useSSORequired';
import { useState } from 'react';
import { getPaywallLink } from 'features/membership/membership.utils';
import { useEnableFlightTrackerLayer } from 'features/mapping/hooks/useEnableFlightTrackerLayer';
import { HttpStatusCode } from 'axios';
import { LogoWithTagline } from 'components/LogoWithTagline';

type FormValues = {
  username: string;
  password: string;
};

const initialValues: FormValues = {
  username: '',
  password: '',
};

export const LoginForm = (): JSX.Element => {
  const history = useHistory();
  const { loginSuccess } = useAuthState();
  const { enableFlightTrackerLayerForMember } = useEnableFlightTrackerLayer();
  const { t } = useTranslation();
  const [ssoUsername, setSSOUsername] = useState('');

  const { ssoRequired, loginUrl, isLoading } = useSSORequired(ssoUsername);

  const validationSchema = Yup.object({
    password: Yup.string()
      .required(t('login.inputs.password.required'))
      .min(3, t('login.inputs.password.error')),
    username: Yup.string().required(t('login.inputs.username.required')),
  });

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const { isPending, mutate } = useMutation({
    mutationFn: (newLogin: FormValues) => API.post('auth/login/', newLogin),
  });

  const handleLogin: SubmitHandler<FormValues> = (values) => {
    mutate(values, {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSuccess: (data: any) => {
        const key = data?.data?.key || null;
        const userData = data?.data?.user || null;
        const params = {
          user: userData,
        };
        registerDeviceWithWatchDuty(params);
        loginSuccess(key, userData);
        enableFlightTrackerLayerForMember(userData);
        history.push('/');
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const status = error.response?.status;
        const data = error.response?.data;

        const expectedErrorStatusCodes = [
          HttpStatusCode.BadRequest,
          HttpStatusCode.TooManyRequests,
        ];

        if (!expectedErrorStatusCodes.includes(status)) {
          logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
          return;
        }

        // Handle 429 rate limiting
        if (status === HttpStatusCode.TooManyRequests && data?.detail) {
          enqueueSnackbar(data.detail, { variant: 'error' });
          return;
        }

        // Handle 400 validation errors
        const nonFieldErrors = data?.nonFieldErrors;
        if (
          status === HttpStatusCode.BadRequest &&
          nonFieldErrors?.length === 1
        ) {
          enqueueSnackbar(nonFieldErrors[0], { variant: 'error' });
          return;
        }

        // Generic error message
        logUnexpectedErrorAndNotifyUser(t, { error });
      },
    });
  };

  const paywallLink = getPaywallLink({
    hasActiveMembership: false,
    source: 'login_page_learn_more',
  });

  return (
    <Stack
      component="form"
      autoComplete="off"
      onSubmit={handleSubmit(handleLogin)}
      noValidate
      spacing={6}
      sx={{ flex: 1 }}
    >
      <Stack spacing={6} sx={{ flex: 1 }}>
        <LogoWithTagline />

        <Stack spacing={2}>
          <Controller
            name="username"
            control={control}
            render={({ field, fieldState }): JSX.Element => {
              const { ref, onChange, ...muiFieldProps } = field;
              return (
                <TextField
                  id="field-control-username"
                  label={t('login.inputs.username.label')}
                  fullWidth
                  type="text"
                  {...muiFieldProps}
                  inputRef={ref}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  required
                  onChange={(e) => {
                    onChange(e);
                    setSSOUsername(e.target.value);
                  }}
                  slotProps={{
                    htmlInput: { autoCapitalize: 'none', autoFocus: true },
                  }}
                />
              );
            }}
          />

          {ssoRequired ? (
            <Button type="submit" fullWidth size="large" href={loginUrl}>
              {t('login.buttons.loginWithSSO')}
            </Button>
          ) : (
            <>
              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }): JSX.Element => {
                  const { ref, ...muiFieldProps } = field;
                  return (
                    <TextField
                      id="field-control-password"
                      label={t('login.inputs.password.label')}
                      fullWidth
                      type="password"
                      {...muiFieldProps}
                      inputRef={ref}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      required
                    />
                  );
                }}
              />

              <Stack spacing={1} sx={{ pt: 2 }}>
                <Button
                  disabled={isPending || isLoading}
                  type="submit"
                  fullWidth
                  size="large"
                >
                  {isPending ? (
                    <CircularProgress size={24} color="secondary" />
                  ) : (
                    t('login.buttons.login')
                  )}
                </Button>

                <Button
                  fullWidth
                  size="large"
                  component={Link}
                  to="/reset_password"
                  variant="text"
                  color="inherit"
                >
                  {t('login.forgotPassword')}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Typography align="center" fontWeight={500}>
          {t('login.notAMember')}
        </Typography>

        <Button
          fullWidth
          size="large"
          to={paywallLink.url}
          component={Link}
          color="neutral"
          variant="outlined"
        >
          {t('login.buttons.learnMore')}
        </Button>
      </Stack>
    </Stack>
  );
};
